import { IProject } from 'models';
import { Renderer, resolveClimatePreset } from 'rewild-renderer';
import {
  MAX_WATER_TYPES,
  LAKE_WATER,
  getWaterTypeIndex,
} from 'rewild-renderer/lib/renderers/terrain/Water';
import {
  TouchedWaterChunk,
  WaterStampType,
  applyWaterStamp,
  editedBody,
  editedBodyId,
} from 'rewild-renderer/lib/renderers/terrain/WaterEdit';
import { writeWaterBodies } from 'src/database/water-bodies';
import { writeWaterEdit } from 'src/database/water-edits';
import { startLevelRef } from 'src/database/local-asset-store';

// Console commands that write water edits the way the water brush will: stamp
// the loaded chunks under a disc, rebuild their water, and save the edits. Also
// the water edit rules: inspect and settle the water at a point. They
// default to the viewer's position.
export function registerWaterEditDevCommands(
  renderer: Renderer,
  project: IProject
) {
  const stamp = async (
    type: WaterStampType,
    x: number | undefined,
    z: number | undefined,
    radius: number,
    level: number | undefined
  ) => {
    const levelRef = startLevelRef(project);
    if (!levelRef) {
      console.warn(
        'No levelId on the current project — water edits not saved.'
      );
      return;
    }

    const terrain = renderer.terrainRenderer;
    const centerX = x ?? terrain.viewerPosition.x;
    const centerZ = z ?? terrain.viewerPosition.z;
    const ground = terrain.sampleHeight(centerX, centerZ);
    const surface = level ?? (ground ?? terrain.seaLevel) + 1.5;

    const span = terrain.chunkSize;
    const half = span / 2;
    const reads: Promise<unknown>[] = [];
    for (
      let cy = Math.ceil((centerZ - radius - half) / span);
      cy <= Math.floor((centerZ + radius + half) / span);
      cy++
    )
      for (
        let cx = Math.ceil((centerX - radius - half) / span);
        cx <= Math.floor((centerX + radius + half) / span);
        cx++
      ) {
        const chunk = terrain.terrainChunks.get(`${cx},${cy}`);
        if (chunk)
          reads.push(chunk.resolveWaterEdit(terrain.waterEditProvider));
      }
    await Promise.all(reads);

    const climate = resolveClimatePreset(terrain.climatePreset);
    const typeWeights = new Array<number>(MAX_WATER_TYPES).fill(0);
    typeWeights[Math.max(0, getWaterTypeIndex(climate, LAKE_WATER))] = 1;

    const source = {
      chunkSize: terrain.mapChunkSizeLod,
      metersPerSample: terrain.metersPerSample,
      getEdit: (cx: number, cy: number) =>
        terrain.terrainChunks.get(`${cx},${cy}`)?.editableWaterEdit() ?? null,
    };
    const bodyId = editedBodyId(
      Math.imul(Math.round(centerX), 73856093) ^
        Math.imul(Math.round(centerZ), 19349663)
    );
    // A reset eases the edit's hold toward the generator; held long enough it
    // lets go of the whole disc.
    const passes = type === 'reset' ? 64 : 1;
    const edited = new Map<string, TouchedWaterChunk>();
    for (let pass = 0; pass < passes; pass++)
      for (const t of applyWaterStamp(source, {
        type,
        centerX,
        centerZ,
        radius,
        amount: 1,
        level: surface,
        bodyId,
        typeWeights,
      }))
        edited.set(`${t.cx},${t.cy}`, t);
    const touched = [...edited.values()];

    await Promise.all(
      touched.map((t) => {
        terrain.refreshChunkWater(t.cx, t.cy);
        return writeWaterEdit(levelRef, t.cx, t.cy, t.edit);
      })
    );
    if (type === 'add' && touched.length > 0) {
      await terrain.waterRules.setRecord(
        editedBody(
          bodyId,
          surface,
          typeWeights.map((w) => w * 255)
        )
      );
      await writeWaterBodies(levelRef, terrain.waterRules.savedBodies());
    }
    console.log(
      `${type}Water: ${
        touched.length
      } chunk(s) edited and saved around (${centerX.toFixed(
        1
      )}, ${centerZ.toFixed(1)}), radius ${radius} m${
        type === 'add' ? `, level ${surface.toFixed(2)} m` : ''
      }.`
    );
  };

  const at = (x: number | undefined, z: number | undefined) => {
    const viewer = renderer.terrainRenderer.viewerPosition;
    return { x: x ?? viewer.x, z: z ?? viewer.z };
  };

  (window as any).waterBody = async (x?: number, z?: number) => {
    const p = at(x, z);
    const report = await renderer.terrainRenderer.waterRules.bodyAt(p.x, p.z);
    if (!report) {
      console.log('No lake owns the ground here.');
      return;
    }
    const { body } = report;
    console.log(
      `Water body ${body.id}: level ${body.level.toFixed(
        2
      )} m, recorded spill ${body.spillHeight.toFixed(2)} m, spill now ${
        Number.isNaN(report.spillHeight)
          ? 'none (no water below its level)'
          : `${report.spillHeight.toFixed(2)} m${
              report.sea ? ' (into the sea)' : ''
            }${report.edge ? ' (search edge)' : ''}`
      }.`
    );
    return report;
  };

  (window as any).settleWater = async (x?: number, z?: number, radius = 20) => {
    const levelRef = startLevelRef(project);
    const terrain = renderer.terrainRenderer;
    const p = at(x, z);
    const settled = await terrain.waterRules.settle(
      p.x - radius,
      p.z - radius,
      p.x + radius,
      p.z + radius
    );
    if (levelRef) {
      await Promise.all(
        settled.chunks.map((t) => writeWaterEdit(levelRef, t.cx, t.cy, t.edit))
      );
      if (settled.outcomes.length > 0)
        await writeWaterBodies(levelRef, terrain.waterRules.savedBodies());
    } else console.warn('No levelId on the current project — not saved.');
    for (const o of settled.outcomes)
      console.log(
        `Water body ${o.body.id}: spill ${o.body.spillHeight.toFixed(2)} m, ${
          o.drained
            ? `drained from ${o.previousLevel.toFixed(
                2
              )} m to ${o.body.level.toFixed(2)} m${
                o.joined ? ', joining the ocean' : ''
              }`
            : `level ${o.body.level.toFixed(2)} m holds`
        }.`
      );
    if (settled.channelTexels > 0)
      console.log(`The sea filled ${settled.channelTexels} texel(s).`);
    if (settled.outcomes.length === 0) console.log('No lake owns this ground.');
  };

  (window as any).addWater = (
    x?: number,
    z?: number,
    radius = 20,
    level?: number
  ) => stamp('add', x, z, radius, level);

  (window as any).removeWater = (x?: number, z?: number, radius = 20) =>
    stamp('remove', x, z, radius, undefined);

  (window as any).resetWater = (x?: number, z?: number, radius = 20) =>
    stamp('reset', x, z, radius, undefined);
}
